import { Injectable, Inject } from '@angular/core';
import { Router } from '@angular/router';
import { AppSettingsUrl } from '../appSettings';
import { AuthOut, TApiOut, UserRoles, apiBase, IApiAuthState, AuthType, ApiState } from './external/api';
import { isSet } from './external/storage';
import { LocalStorageService } from './localStorage.service';
import { SessionStorageService } from './sessionStorage.service';
import { AsyncService } from './async.service';
import { authApi } from './external/api/auth';
import { signUpOptions, UserSettings } from './external/config/user';
import { userApi } from './external/api/user';

import { Auth, AuthProvider, FacebookAuthProvider, getAuth, getRedirectResult, GoogleAuthProvider, OAuthCredential, OAuthProvider, signInWithCredential, signInWithPopup, TwitterAuthProvider, User, UserCredential } from "@firebase/auth";
import { initializeApp } from "@firebase/app";
import { TranslationService } from './translation.service';
import { IDDLOption } from '../components/ddl-option/ddl-option.component';
import { hub } from './external/hub';

export interface IUserAuth {
    userName: string;
    password: string;
}

export interface IUser {
    settings: UserSettings;
    layout: any;
    authOut: AuthOut;
    authState: IApiAuthState;
}
export interface ISocialResponse {
    token: string,
    type: AuthType
    error: string;
}

@Injectable()
export class AuthService {

    public static currentUser: string = "currentUser";

    private user?: IUser;
    msProvider: OAuthProvider;
    fbProvider: FacebookAuthProvider;
    twitterProvider: TwitterAuthProvider;
    googleProvider: GoogleAuthProvider;
    auth: Auth;

    firebaseConfig = {
        apiKey: "AIzaSyBryKmMl6ToEl5A8DKqbqXOWAK8tf6UC6g",
        authDomain: "marktools-social.firebaseapp.com",
        projectId: "marktools-social"
    };

    constructor(private router: Router,
        private localStorage: LocalStorageService,
        private sessionStorage: SessionStorageService,
        private asyncSrv: AsyncService,
        private trans: TranslationService
    ) {
        apiBase.setAuthUrl(AppSettingsUrl.apiUrl);
        this.user = this.localStorage.getObject<IUser>(AuthService.currentUser, true);
        apiBase.authState = this.user?.authState;
        this.updateStorageUser();
        this.initSocial();
    }

    private initSocial() {
        const app = initializeApp(this.firebaseConfig);
        this.msProvider = new OAuthProvider('microsoft.com');
        this.msProvider.addScope("email");

        this.fbProvider = new FacebookAuthProvider();
        this.fbProvider.addScope("email");

        this.twitterProvider = new TwitterAuthProvider();
        this.twitterProvider.addScope("users.read");
        this.twitterProvider.addScope("email");

        this.googleProvider = new GoogleAuthProvider();
        //this.googleProvider.addScope('https://www.googleapis.com/auth/userinfo.email');
        this.googleProvider.addScope("email");

        // Initialize Firebase Authentication and get a reference to the service
        this.auth = getAuth(app);

        this.auth.onIdTokenChanged(async (user) => {
            if (!this.user || !user)
                return;
            const provider = this.getProviderByAuthType(apiBase.state.bearerType);
            if (!provider || user.providerId != provider.providerId)
                return;
            this.updateBearer(await user.getIdToken());
        });
        apiBase.renewTokenHandler = () => this.renewSocialToken();
    }

    public InitSettings = (settings: UserSettings) => {
        this.user.settings = settings;
        //console.log("user.settings");
    }

    public getUserData = (): IUser => {
        return this.user;
    }
    private hasUserRole(user: IUser, role: UserRoles) {
        return isSet(user.authOut.tls, role);
    }
    public resetRole(role: UserRoles) {
        if (isSet(this.user.authOut.tls, role)) {
            this.user.authOut.tls ^= role;
            this.localStorage.saveObject(AuthService.currentUser, this.user, true);
        }
    }

    public get canManageData() { return this.hasUserRole(this.user, UserRoles.ManageData); }
    public get isAdmin() { return this.hasUserRole(this.user, UserRoles.Admin); }
    public get isAgreed() { return this.hasUserRole(this.user, UserRoles.Agreed); }
    public get isParnter() { return this.hasUserRole(this.user, UserRoles.Partner); }
    public get isUnverified() { return this.hasUserRole(this.user, UserRoles.Unverified); }

    private updateStorageUser() {
        this.sessionStorage.setUserId(this.user?.authOut?.uid);
        this.localStorage.setUserId(this.user?.authOut?.uid);
    }
    private roleNavigationSwitch = (): void => {
        if (!this.user)
            window.location.pathname = 'auth/signin';
        else
            window.location.assign('user/dashboard');
    }

    public logout = () => {
        this.user = undefined;
        this.localStorage.removeData(AuthService.currentUser, true);
        this.updateStorageUser();
        this.roleNavigationSwitch();
    }

    public restorePassword = (login: string): void => {
        //TBD
    }
    public agreeTermsOfService = () => userApi.agreeTermsOfService().then(auth => {
        this.user.authOut = auth;
        this.localStorage.saveObject(AuthService.currentUser, this.user, true);
    });

    public verify = (token: string, authType: AuthType, email: string) => authApi.verifyEmail(token, authType, email, this.trans.lang);

    public logIn = (userData: IUserAuth) => this.signInResponse(AuthType.Email, authApi.signIn(userData.userName?.trim()?.toLowerCase() ?? '', userData.password));

    public externalLogIn = (token: string, authType: AuthType) => this.signInResponse(authType, authApi.externalSignIn(token, authType));

    public signUp = (token: string, authType: AuthType, options: signUpOptions) => this.signInResponse(authType, authApi.signUp(token, authType, options));

    private signInResponse = (authType: AuthType, p: Promise<AuthOut>) => {
        return new Promise<void>(async (res, rej) => {
            await p.catch(x => {
                this.user = null;
                rej(x);
                return null as AuthOut;
            }).then(async x => {
                if (x == null)
                    return;
                if (authType != AuthType.Email)
                    apiBase.state.bearer = await this.auth.currentUser.getIdToken();
                this.user = {
                    authState: apiBase.authState,
                    authOut: x
                } as IUser;
                x.domain = x.domain?.toLocaleLowerCase();
                const host = location.hostname.toLocaleLowerCase();
                if (x.domain != host) {
                    let data = JSON.stringify(this.user);
                    data = btoa(data);
                    if (AppSettingsUrl.debug)
                        window.location.href = `https://localhost:4200/auth?sid=${data}`;
                    else
                        window.location.href = `https://${x.domain}/auth?sid=${data}`;
                    res();
                    return;

                }
                this.updateStorageUser();
                this.localStorage.saveObject(AuthService.currentUser, this.user, true);
                this.roleNavigationSwitch();
                res();
            });
        });

    }

    public restore(sid: string) {
        sid = atob(sid);
        this.user = JSON.parse(sid) as IUser;
        apiBase.authState = this.user.authState;
        this.updateStorageUser();
        this.localStorage.saveObject(AuthService.currentUser, this.user, true);
        this.roleNavigationSwitch();
    }

    private renewSocialToken = () =>
        new Promise<boolean>(async (res) => {
            let ok = false;
            if (this.user == null) {
                res(ok);
                return;
            }
            try {
                for (let i = 0; i < 10; i++) {
                    if (this.auth.currentUser)
                        break;
                    await new Promise((x) => setTimeout(() => x(null), 1000));
                }

                const token = await this.auth.currentUser.getIdToken();
                if (this.user == null) {
                    res(ok);
                    return;
                }
                if (token) {
                    this.updateBearer(token);
                    ok = true;
                }
            } catch (e) {
                console.error(e);
            }
            res(ok);
        });

    private updateBearer(token: string) {
        apiBase.state.bearer = token;
        this.user.authState = apiBase.authState;
        this.localStorage.saveObject(AuthService.currentUser, this.user, true);
    }
    private getProviderByAuthType(authType: AuthType) {
        switch (authType) {
            case AuthType.Google:
                return this.googleProvider;
            case AuthType.Microsoft:
                return this.msProvider;
            case AuthType.Twitter:
                return this.twitterProvider;
            case AuthType.Facebook:
                return this.fbProvider;
        }
        return null;
    }

    public signInSocial = (authType: AuthType) => {
        const provider = this.getProviderByAuthType(authType);
        if (!provider)
            throw 'not supported';
        return this.socialSignInInternal(authType, this.getProviderByAuthType(authType));
    }

    private socialSignInInternal = (authType: AuthType, provider: AuthProvider) => new Promise<ISocialResponse>(async (res, rej) => {
        try {
            const result = await signInWithPopup(this.auth, provider);
            let token: string = null;
            switch (authType) {
                case AuthType.Google:
                    const creds = GoogleAuthProvider.credentialFromResult(result);
                    token = creds.idToken;
                    break;
                case AuthType.Microsoft: {
                    const creds = OAuthProvider.credentialFromResult(result);
                    token = creds.idToken;
                } break;
                default:
                    {
                        const creds = OAuthProvider.credentialFromResult(result);
                        token = creds.idToken;
                    }
            }
            res({
                type: authType,
                token: token,
                error: null
            });
        } catch (error) {
            res({
                type: authType,
                token: null,
                error: error as string
            });
        }
    });
}
